/**
 * Landing Page
 * WhatsApp Widget Library Homepage
 */

import { useNavigate } from "react-router-dom";
import {
  MessageSquare,
  Palette,
  Layout,
  Users,
  Clock,
  Code,
  ArrowRight,
  Check,
  Star,
  Zap,
} from "lucide-react";
import { Button } from "@/design-system/components";
import { colors } from "@/design-system/theme/colors";

const FEATURES = [
  {
    icon: Palette,
    title: "Beautiful Themes",
    description: "Professional designs that match any brand identity.",
  },
  {
    icon: Layout,
    title: "Flexible Position",
    description: "Place widgets anywhere on your website.",
  },
  {
    icon: MessageSquare,
    title: "Chat Preview",
    description: "Engage visitors with welcome messages.",
  },
  {
    icon: Users,
    title: "Multi-Agent",
    description: "Route customers to the right team.",
  },
  {
    icon: Clock,
    title: "Business Hours",
    description: "Show availability status automatically.",
  },
  {
    icon: Code,
    title: "Easy Setup",
    description: "One line of code to get started.",
  },
];

const BENEFITS = [
  "Increase engagement by 40%",
  "Instant customer response",
  "Capture leads on WhatsApp",
  "Works on all devices",
];

export function LandingPage() {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate("/templates");
  };

  return (
    <div style={{ backgroundColor: colors.white }}>
      {/* Hero Section */}
      <section
        className="relative overflow-hidden"
        style={{
          background: `linear-gradient(180deg, ${colors.primaryExtraLight} 0%, ${colors.white} 100%)`,
        }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              {/* Trust Badge */}
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6 transition-all duration-200 hover:shadow-md cursor-default select-none"
                style={{
                  backgroundColor: colors.white,
                  border: `1px solid ${colors.surfaceSubtle}`,
                }}
              >
                <Star size={14} color={colors.warning} fill={colors.warning} />
                <span
                  style={{
                    color: colors.textDefault,
                    fontWeight: 500,
                    fontSize: 13,
                  }}
                >
                  Trusted by 10,000+ businesses
                </span>
              </div>

              {/* Headline */}
              <h1
                className="mb-4"
                style={{
                  fontSize: 40,
                  fontWeight: 700,
                  lineHeight: 1.2,
                  color: colors.textInverse,
                  letterSpacing: "-0.02em",
                }}
              >
                WhatsApp Widgets for Your Website
              </h1>

              {/* Subheadline */}
              <p
                className="mb-8 max-w-lg mx-auto lg:mx-0"
                style={{
                  fontSize: 16,
                  lineHeight: 1.6,
                  color: colors.textMuted,
                }}
              >
                Convert visitors into customers with beautiful chat widgets.
                Customize colors, add your branding, and install in minutes.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start mb-8">
                <Button
                  variant="primary"
                  size="large"
                  rightIcon={<ArrowRight size={18} />}
                  onClick={handleGetStarted}
                >
                  Get Started Free
                </Button>
                <Button
                  variant="secondary"
                  size="large"
                  onClick={handleGetStarted}
                >
                  View Templates
                </Button>
              </div>

              {/* Benefits */}
              <div className="grid grid-cols-2 gap-2">
                {BENEFITS.map((benefit) => (
                  <div
                    key={benefit}
                    className="flex items-center gap-2 select-none cursor-default transition-all duration-200 hover:translate-x-1"
                  >
                    <div
                      className="w-4 h-4 rounded-full flex items-center justify-center shrink-0"
                      style={{ backgroundColor: colors.successBgSubtle }}
                    >
                      <Check size={10} color={colors.success} strokeWidth={3} />
                    </div>
                    <span
                      style={{
                        color: colors.textDefault,
                        fontSize: 13,
                        fontWeight: 500,
                      }}
                    >
                      {benefit}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Widget Preview */}
            <div className="relative hidden lg:block">
              <div
                className="relative mx-auto w-full max-w-sm p-5 rounded-xl"
                style={{
                  backgroundColor: colors.white,
                  boxShadow: "0 20px 40px -12px rgba(0, 0, 0, 0.12)",
                  border: `1px solid ${colors.surfaceSubtle}`,
                }}
              >
                {/* Browser Header */}
                <div
                  className="flex items-center gap-1.5 mb-4 pb-3 border-b select-none"
                  style={{ borderColor: colors.surfaceLight }}
                >
                  <div
                    className="w-2.5 h-2.5 rounded-full cursor-default"
                    style={{ backgroundColor: "#FF5F57" }}
                  />
                  <div
                    className="w-2.5 h-2.5 rounded-full cursor-default"
                    style={{ backgroundColor: "#FFBD2E" }}
                  />
                  <div
                    className="w-2.5 h-2.5 rounded-full cursor-default"
                    style={{ backgroundColor: "#28CA41" }}
                  />
                  <div
                    className="flex-1 h-5 rounded ml-3"
                    style={{ backgroundColor: colors.surfaceLighter }}
                  />
                </div>

                {/* Mock Content */}
                <div className="space-y-2 mb-16">
                  <div
                    className="h-3 rounded"
                    style={{
                      backgroundColor: colors.surfaceLighter,
                      width: "75%",
                    }}
                  />
                  <div
                    className="h-3 rounded"
                    style={{
                      backgroundColor: colors.surfaceLighter,
                      width: "55%",
                    }}
                  />
                  <div
                    className="h-16 rounded mt-3"
                    style={{ backgroundColor: colors.surfaceLighter }}
                  />
                  <div
                    className="h-3 rounded"
                    style={{
                      backgroundColor: colors.surfaceLighter,
                      width: "65%",
                    }}
                  />
                </div>

                {/* Widget Preview */}
                <div className="absolute bottom-5 right-5">
                  <div
                    className="w-64 rounded-xl overflow-hidden"
                    style={{
                      backgroundColor: colors.white,
                      boxShadow: "0 8px 30px rgba(0, 0, 0, 0.12)",
                    }}
                  >
                    {/* Header */}
                    <div
                      className="p-3 select-none"
                      style={{ backgroundColor: "#25D366" }}
                    >
                      <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center cursor-default">
                          <span className="text-white text-sm font-semibold">
                            N
                          </span>
                        </div>
                        <div>
                          <div className="text-white font-semibold text-sm">
                            Notifyy
                          </div>
                          <div className="text-white/80 text-xs">Online</div>
                        </div>
                      </div>
                    </div>
                    {/* Message */}
                    <div
                      className="p-3 select-none"
                      style={{ backgroundColor: "#ECE5DD" }}
                    >
                      <div
                        className="bg-white px-3 py-2 rounded-lg text-xs max-w-[85%] cursor-default"
                        style={{ color: colors.textInverse }}
                      >
                        Hi! How can we help you today?
                      </div>
                    </div>
                    {/* Button */}
                    <div
                      className="p-2.5"
                      style={{ backgroundColor: colors.white }}
                    >
                      <button
                        className="w-full py-2 rounded-lg text-white text-sm font-medium transition-all duration-200 cursor-pointer hover:opacity-90 hover:scale-[1.02] active:scale-[0.98]"
                        style={{ backgroundColor: "#25D366" }}
                      >
                        Start Chat
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section
        className="py-16"
        style={{ backgroundColor: colors.surfaceLightest }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          {/* Section Header */}
          <div className="text-center mb-12">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4 select-none cursor-default"
              style={{ backgroundColor: colors.primaryExtraLight }}
            >
              <Zap size={14} color={colors.primary} />
              <span
                style={{
                  color: colors.primary,
                  fontWeight: 600,
                  fontSize: 12,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Features
              </span>
            </div>
            <h2
              style={{
                fontSize: 28,
                fontWeight: 700,
                color: colors.textInverse,
                marginBottom: 8,
              }}
            >
              Everything You Need
            </h2>
            <p
              style={{
                fontSize: 15,
                color: colors.textMuted,
                maxWidth: 480,
                margin: "0 auto",
              }}
            >
              Powerful features to engage customers and grow your business
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((feature) => (
              <div
                key={feature.title}
                className="p-5 rounded-xl transition-all duration-300 hover:shadow-lg hover:-translate-y-1 cursor-default select-none"
                style={{ backgroundColor: colors.white }}
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
                  style={{ backgroundColor: colors.primaryExtraLight }}
                >
                  <feature.icon size={20} color={colors.primary} />
                </div>
                <h3
                  style={{
                    fontSize: 15,
                    fontWeight: 600,
                    color: colors.textInverse,
                    marginBottom: 4,
                  }}
                >
                  {feature.title}
                </h3>
                <p
                  style={{
                    fontSize: 13,
                    color: colors.textMuted,
                    lineHeight: 1.5,
                  }}
                >
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="py-16"
        style={{
          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
        }}
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2
            style={{
              fontSize: 28,
              fontWeight: 700,
              color: colors.white,
              marginBottom: 12,
            }}
          >
            Ready to Get Started?
          </h2>
          <p
            style={{
              fontSize: 15,
              color: colors.white,
              opacity: 0.9,
              marginBottom: 24,
              maxWidth: 400,
              margin: "0 auto 24px",
            }}
          >
            Create your first WhatsApp widget in minutes. No credit card
            required.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              variant="secondary"
              size="large"
              rightIcon={<ArrowRight size={18} />}
              onClick={handleGetStarted}
              style={{ backgroundColor: colors.white, color: colors.primary }}
            >
              Start Free
            </Button>
            <Button
              variant="outline"
              size="large"
              onClick={handleGetStarted}
              style={{
                borderColor: "rgba(255,255,255,0.5)",
                color: colors.white,
              }}
            >
              View Templates
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer
        className="py-6 px-4 sm:px-6 text-center select-none"
        style={{ borderTop: `1px solid ${colors.surfaceSubtle}` }}
      >
        <p
          className="cursor-default"
          style={{ fontSize: 13, color: colors.textMuted }}
        >
          &copy; {new Date().getFullYear()} Notifyy. All rights reserved.
        </p>
      </footer>
    </div>
  );
}

export default LandingPage;
